/**
 * Detalhe da fórmula do motor (U4) — documentação pt-BR em card legível (o que
 * calcula, base legal, parâmetros consumidos com LINK direto para a tela de
 * Parâmetros) + edição de metadados (descrição e vigência, com gate da API).
 */
import { useState } from "react";
import { Anchor, Badge, Button, Card, Group, Text, Title } from "@mantine/core";
import { IconBook, IconLink } from "@tabler/icons-react";
import { api, ErroApi } from "../../api";
import { fmtCompetencia } from "../../format";
import { CompetenciaField } from "../../componentes/entradas-mantine";
import { notificarOk } from "../../componentes/notificacoes";

export interface Formula {
  id: string;
  chave: string;
  versao: string;
  implementacao: string;
  vigenciaInicio: number;
  vigenciaFim: number | null;
  descricao: string | null;
  documentacao: {
    titulo: string;
    texto: string;
    baseLegal: string[];
    parametrosConsumidos: string[];
  } | null;
}

export function FormulaDetalhe({
  formula,
  onFechar,
  onSalvo,
}: {
  formula: Formula;
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const [descricao, setDescricao] = useState(formula.descricao ?? "");
  const [vigIni, setVigIni] = useState<number | null>(formula.vigenciaInicio);
  const [vigFim, setVigFim] = useState<number | null>(formula.vigenciaFim);
  const [erro, setErro] = useState<string | null>(null);
  const doc = formula.documentacao;

  async function salvar() {
    setErro(null);
    try {
      await api.patch(`/formulas/${formula.id}`, { descricao: descricao || null, vigenciaInicio: vigIni ?? undefined, vigenciaFim: vigFim });
      notificarOk("Metadados da fórmula atualizados.");
      onSalvo();
      onFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  return (
    <Card withBorder shadow="xs" mt="md">
      <Group justify="space-between" mb="sm">
        <Title order={4}>
          {formula.chave} <Text span ff="monospace" size="sm" c="dimmed">{formula.versao}</Text>
        </Title>
        <Button variant="subtle" size="xs" onClick={onFechar}>
          fechar detalhe
        </Button>
      </Group>

      {doc ? (
        <Card withBorder mb="md" bg="gray.0">
          <Group gap="xs" mb={4}>
            <IconBook size={16} />
            <Text fw={700}>{doc.titulo}</Text>
          </Group>
          <Text size="sm" mb="sm">{doc.texto}</Text>
          <Group gap="xs" mb="sm" wrap="wrap">
            {doc.baseLegal.map((b) => (
              <Badge key={b} variant="light" color="sillis">
                {b}
              </Badge>
            ))}
          </Group>
          {doc.parametrosConsumidos.length > 0 && (
            <Group gap="xs" wrap="wrap">
              <Text size="sm" fw={600}>
                Parâmetros consumidos:
              </Text>
              {doc.parametrosConsumidos.map((p) => (
                <Anchor key={p} href="#/parametros" size="sm">
                  <Badge variant="outline" leftSection={<IconLink size={10} />}>
                    {p}
                  </Badge>
                </Anchor>
              ))}
            </Group>
          )}
        </Card>
      ) : (
        <Text size="sm" c="dimmed" mb="md">
          Sem documentação cadastrada para esta versão (cadastre em api/formulas-docs.ts).
        </Text>
      )}

      <Title order={5} mb="sm">Metadados (edição vale daqui pra frente)</Title>
      <Text size="sm" fw={600} mb={2}>
        Descrição
      </Text>
      <Text size="xs" c="dimmed" mb={2}>
        Resumo da fórmula exibido nas listas (o código da fórmula é imutável por aqui).
      </Text>
      <textarea
        rows={3}
        value={descricao}
        onChange={(e) => setDescricao(e.currentTarget.value)}
        style={{ width: "100%", marginBottom: 12, font: "inherit", padding: "6px 8px", border: "1px solid #b9c0c8", borderRadius: 6 }}
      />
      <Group align="flex-end" mb="sm" wrap="wrap">
        <div>
          <Text size="sm" fw={600} mb={2}>Vigência de</Text>
          <CompetenciaField valor={vigIni} onChange={setVigIni} />
        </div>
        <div>
          <Text size="sm" fw={600} mb={2}>até (vazio = em vigor)</Text>
          <CompetenciaField valor={vigFim} onChange={setVigFim} anoMin={2025} anoMax={2030} />
          {vigFim !== null && (
            <Button variant="subtle" size="xs" onClick={() => setVigFim(null)}>
              limpar fim
            </Button>
          )}
        </div>
        <Text size="xs" c="dimmed">
          Vigência atual: {fmtCompetencia(formula.vigenciaInicio)} → {formula.vigenciaFim ? fmtCompetencia(formula.vigenciaFim) : "em vigor"}.
          Fechar a vigência de fórmula exigida por competência aberta é bloqueado (a API explica).
        </Text>
      </Group>
      {erro && (
        <Text size="sm" c="red.7" mb="sm" role="alert">
          {erro}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>
          Voltar
        </Button>
        <Button onClick={() => void salvar()}>Salvar metadados</Button>
      </Group>
    </Card>
  );
}
